import styles from './FadeToastUp.module.scss';

// convert to milliseconds
const STAGGER = parseFloat(styles.stagger) * 1000;

import cx from 'classnames';
import React from 'react';

import { ScrollAnimator } from '../ScrollAnimator/ScrollAnimator';

type CallbackArgs<E> = {
  style: any;
  ref: React.Ref<E>;
  className: string;
};
type Callback<E> = (args: CallbackArgs<E>) => React.ReactNode;

interface IFadeToastUp<E extends HTMLElement = HTMLElement> {
  delay?: number;
  transitionDuration: number;
  delayMultiplier: number;
  children: Callback<E> | React.ReactElement<React.AllHTMLAttributes<E>>;
  render?: Callback<E>;
  threshold?: number;
  disableAnimation?: boolean;
}

/**
 * If you want to add a new state:
 *  Add it to `initialState` and to `ScrollAnimatorComponentProps`
 *  Pass the new prop value in `renderProps` in the `render` method
 */

const initialState = {
  className: styles.fadeToastUp,
};
type ComponentState = Readonly<typeof initialState>;

export class FadeToastUp<E extends HTMLElement = HTMLElement> extends React.Component<
  IFadeToastUp<E>,
  ComponentState
> {
  static defaultProps = {
    transitionDuration: 600,
    delay: STAGGER,
    delayMultiplier: 0,
  };

  readonly state: ComponentState = initialState;

  render() {
    const {
      children,
      delay = 0,
      delayMultiplier,
      disableAnimation,
      threshold,
      transitionDuration,
      render,
    } = this.props;

    if (disableAnimation) {
      const child = render || children;

      return typeof child === 'function'
        ? child({ style: {}, ref: React.createRef<E>(), className: '' })
        : children;
    }

    return (
      <ScrollAnimator delay={delay * delayMultiplier} threshold={threshold}>
        {({ isInView, ref }) => {
          const child = render || children;

          if (typeof child === 'function') {
            return child({
              style: { transitionDuration: `${transitionDuration}ms` },
              className: cx(styles.fadeToastUp, {
                [styles.fadeToastUpActive]: isInView,
              }),
              ref: ref as React.Ref<E>,
            });
          }

          if (!React.isValidElement(children)) return null;

          const { style, className } = children.props;

          return React.cloneElement(children, {
            style: { transitionDuration: `${transitionDuration}ms`, ...style },
            className: cx(className, styles.fadeToastUp, {
              [styles.fadeToastUpActive]: isInView,
            }),
            ref,
          });
        }}
      </ScrollAnimator>
    );
  }
}
